import React, { useCallback, useEffect, useState } from 'react';
import { Select, Switch, Table } from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import type { SorterResult } from 'antd/es/table/interface';
import { getPracticeProviderOverviewList } from 'api/group';
import s from './s.module.less';

type TProviderOverviewItem = {
    providerId: number;
    providerName: string;
    providerCredential: string;
    providerAvatar: string;
    rating: number | null;
    totalReviews: number;
    waitingForReply: number;
    disputed: number;
    prevRating: number | null;
    prevTotalReviews: number | null;
    prevWaitingForReply: number | null;
    prevDisputed: number | null;
};

const SORT_FIELD_MAP: Record<string, string> = {
    rating: 'RATING',
    totalReviews: 'TOTAL',
    waitingForReply: 'WAITING',
    disputed: 'DISPUTED',
};

const getInitials = (name: string) =>
    name.split(' ').map((n) => n.charAt(0)).join('').toUpperCase().slice(0, 2);

const renderDelta = (cur: number | null, prev: number | null, invert?: boolean) => {
    if (prev === null || prev === undefined) return null;
    const d = (cur ?? 0) - prev;
    if (d === 0) return null;
    const isFloat = typeof cur === 'number' && cur % 1 !== 0;
    const sign = d > 0 ? '+' : '';
    const val = isFloat ? d.toFixed(1) : d;
    const cls = invert ? (d > 0 ? s.deltaNeg : s.deltaPos) : (d > 0 ? s.deltaPos : s.deltaNeg);
    return <span className={`${s.tdDelta} ${cls}`}>{sign}{val}</span>;
};

const ProviderReviewsTable: React.FC = () => {
    const [data, setData] = useState<TProviderOverviewItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(5);
    const [sortField, setSortField] = useState<string>('RATING');
    const [sortOrder, setSortOrder] = useState<string>('DESC');
    const [compareOn, setCompareOn] = useState(false);
    const [compareTo, setCompareTo] = useState<string>('LAST_WEEK');

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getPracticeProviderOverviewList({
                current: page,
                size: pageSize,
                sortField,
                sortOrder,
                compareTo: compareOn ? compareTo : undefined,
            });
            if (res && !res.error && res.data?.data) {
                setData(res.data.data.list || []);
                setTotal(res.data.data.totalRecords || 0);
            }
        } catch (e) {
            console.error(e);
        }
        setLoading(false);
    }, [page, pageSize, sortField, sortOrder, compareOn, compareTo]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleTableChange = (
        pagination: TablePaginationConfig,
        _filters: Record<string, any>,
        sorter: SorterResult<TProviderOverviewItem> | SorterResult<TProviderOverviewItem>[],
    ) => {
        if (pagination.current) setPage(pagination.current);
        if (pagination.pageSize) setPageSize(pagination.pageSize);

        const s0 = Array.isArray(sorter) ? sorter[0] : sorter;
        if (s0?.field) {
            const key = String(s0.field);
            setSortField(SORT_FIELD_MAP[key] || 'RATING');
            setSortOrder(s0.order === 'ascend' ? 'ASC' : 'DESC');
        }
    };

    const handleCompareToggle = (checked: boolean) => {
        setCompareOn(checked);
        setPage(1);
    };

    const handleCompareSelect = (val: string) => {
        setCompareTo(val);
        if (val) {
            setCompareOn(true);
        }
        setPage(1);
    };

    const columns: ColumnsType<TProviderOverviewItem> = [
        {
            title: 'Provider',
            dataIndex: 'providerName',
            key: 'name',
            render: (_, record) => (
                <div className={s.provCell}>
                    <div className={s.provAvatar}>
                        {record.providerAvatar
                            ? <img src={record.providerAvatar} alt="" className={s.provAvatarImg} />
                            : getInitials(record.providerName || '')}
                    </div>
                    <div>
                        <div className={s.provName}>{record.providerName}</div>
                        <div className={s.provCred}>{record.providerCredential}</div>
                    </div>
                </div>
            ),
        },
        {
            title: 'Rating',
            dataIndex: 'rating',
            key: 'rating',
            sorter: true,
            defaultSortOrder: 'descend',
            render: (val: number | null, record) => (
                <div className={s.ratingCell}>
                    <svg width="14" height="14" viewBox="0 0 18 17" fill="#FFCB45">
                        <path d="M9 0l2.76 5.59 6.17.9-4.47 4.35 1.06 6.16L9 14.14 3.48 17l1.06-6.16L.07 6.49l6.17-.9L9 0z" />
                    </svg>
                    <span className={s.ratingVal}>{(val ?? 0).toFixed(1)}</span>
                    {compareOn && renderDelta(val, record.prevRating)}
                </div>
            ),
        },
        {
            title: 'Total reviews',
            dataIndex: 'totalReviews',
            key: 'totalReviews',
            sorter: true,
            render: (val: number, record) => (
                <span>
                    {val}
                    {compareOn && renderDelta(val, record.prevTotalReviews)}
                </span>
            ),
        },
        {
            title: 'Waiting for reply',
            dataIndex: 'waitingForReply',
            key: 'waitingForReply',
            sorter: true,
            render: (val: number, record) => (
                <span>
                    <span className={val > 0 ? s.waitingVal : ''}>{val}</span>
                    {compareOn && renderDelta(val, record.prevWaitingForReply, true)}
                </span>
            ),
        },
        {
            title: 'Disputed',
            dataIndex: 'disputed',
            key: 'disputed',
            sorter: true,
            render: (val: number, record) => (
                <span>
                    <span className={val > 0 ? s.disputedVal : ''}>{val}</span>
                    {compareOn && renderDelta(val, record.prevDisputed, true)}
                </span>
            ),
        },
    ];

    return (
        <div className={s.wrap}>
            <div className={s.sectionHdr}>
                <span className={s.sectionTitle}>Providers</span>
                <div className={s.compareWrap}>
                    <span className={s.compareLabel}>Compare to</span>
                    <Select
                        value={compareOn ? compareTo : undefined}
                        placeholder="Off"
                        onChange={handleCompareSelect}
                        allowClear
                        onClear={() => { setCompareOn(false); setPage(1); }}
                        className={s.compareSelect}
                        size="small"
                        options={[
                            { value: 'LAST_WEEK', label: 'Last week' },
                            { value: 'LAST_MONTH', label: 'Last month' },
                        ]}
                    />
                    <Switch
                        checked={compareOn}
                        onChange={handleCompareToggle}
                        size="small"
                    />
                </div>
            </div>
            <Table
                columns={columns}
                dataSource={data}
                rowKey="providerId"
                loading={loading}
                pagination={{ current: page, pageSize, total, size: 'small' }}
                onChange={handleTableChange}
                className={s.table}
            />
        </div>
    );
};

export default ProviderReviewsTable;
